import {
  HARD_MOODS,
  MOODS,
  type Mood,
  addDays,
  todayIso,
  useBirthdays,
  useClubMemberships,
  useClubs,
  useIsOnline,
  useMoodHistory,
  useRewardWallet,
  useSetMood,
} from '@shkola/api';
import { type CutSticker, REWARD_BUDDIES, REWARD_FRAMES, tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CakeIcon,
  CalendarDotsIcon,
  CaretRightIcon,
  PencilSimpleIcon,
  PlusIcon,
  PuzzlePieceIcon,
  SmileyIcon,
} from '../../design/icons';
import { Sticker } from '../../design/kit';
import { buttonClass, useAge } from '../../design/ui';
import { NowCard } from './GlassHome';
import { MOOD_TONES, MoodFace } from './GlassMood';
import { type StudentContext, dayLong, dayMonthLong, weekdayShort } from './model';

/*
 * The header of «Скло 2» (his words 27.10: «приветствие скучное и пустое, нет аватарки; цвет как у
 * трекера месячных; по принципу профиля LinkedIn/X — картинка на фоне, вырез под аватар, имя; стикеры;
 * рядом «Зараз»; разделить тонкой серой полоской»; refs a LinkedIn card and «Kaia Bloom»): one white
 * block — the cover the student picks (vivid, not pastel), the face cut into it with the student's
 * stickers around (the buddies of «Магазин нагород», the frame from there too), the name and the class,
 * tags (the clubs, a birthday near), the mood as a button; beside them, behind a hairline, what goes on
 * now and what is next.
 */

/** The covers to pick from: bright and of today, each its own mood. */
export const COVERS = {
  wave: [
    'radial-gradient(55% 130% at 6% 0%, #4F7BFF 0%, rgb(79 123 255 / 0) 70%)',
    'radial-gradient(45% 150% at 52% 115%, #2FD3C3 0%, rgb(47 211 195 / 0) 70%)',
    'radial-gradient(42% 130% at 100% 0%, #D4F25C 0%, rgb(212 242 92 / 0) 70%)',
    'linear-gradient(105deg, #5B7CFF 0%, #3BC4E6 52%, #A9EC7E 100%)',
  ].join(', '),
  sunrise: [
    'radial-gradient(50% 140% at 0% 100%, #FF6B4A 0%, rgb(255 107 74 / 0) 70%)',
    'radial-gradient(45% 130% at 42% 0%, #FFC83D 0%, rgb(255 200 61 / 0) 70%)',
    'radial-gradient(50% 150% at 100% 60%, #48D6E8 0%, rgb(72 214 232 / 0) 70%)',
    'linear-gradient(100deg, #FF8A4C 0%, #FFC94A 50%, #6FD9E4 100%)',
  ].join(', '),
  notebook: [
    'linear-gradient(rgb(255 255 255 / 0.2) 1px, transparent 1px) 0 0 / 22px 22px',
    'linear-gradient(90deg, rgb(255 255 255 / 0.2) 1px, transparent 1px) 0 0 / 22px 22px',
    'linear-gradient(115deg, #3D6BF2 0%, #5E86FF 55%, #8AA9FF 100%)',
  ].join(', '),
  space: [
    'radial-gradient(1.6px 1.6px at 12% 28%, #FFFFFF 50%, transparent 52%)',
    'radial-gradient(1.2px 1.2px at 26% 70%, #FFFFFF 50%, transparent 52%)',
    'radial-gradient(1.8px 1.8px at 41% 22%, #FFFFFF 50%, transparent 52%)',
    'radial-gradient(1.2px 1.2px at 58% 64%, #FFFFFF 50%, transparent 52%)',
    'radial-gradient(1.6px 1.6px at 73% 30%, #FFFFFF 50%, transparent 52%)',
    'radial-gradient(1.2px 1.2px at 88% 76%, #FFFFFF 50%, transparent 52%)',
    'radial-gradient(60% 130% at 85% 0%, #A174FF 0%, rgb(161 116 255 / 0) 70%)',
    'linear-gradient(115deg, #24307A 0%, #4B3BC4 55%, #8A55E8 100%)',
  ].join(', '),
  meadow: [
    'radial-gradient(50% 140% at 0% 0%, #1FC27D 0%, rgb(31 194 125 / 0) 70%)',
    'radial-gradient(50% 140% at 100% 100%, #F9E04B 0%, rgb(249 224 75 / 0) 70%)',
    'linear-gradient(105deg, #2BC98A 0%, #8EE05A 55%, #F2E45A 100%)',
  ].join(', '),
} as const;

export type Cover = keyof typeof COVERS;
const COVER_NAMES = Object.keys(COVERS) as Cover[];

/** The cover the student picked, kept in this browser for now (later — with the rewards, in the base). */
function useCover(memberId: string) {
  const key = `shkola.cover.${memberId}`;
  const [cover, setCover] = useState<Cover>(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved && (COVER_NAMES as string[]).includes(saved)) return saved as Cover;
    } catch {
      // private mode: the first cover
    }
    return 'wave';
  });
  const choose = (next: Cover) => {
    setCover(next);
    try {
      localStorage.setItem(key, next);
    } catch {
      // only for this visit
    }
  };
  return [cover, choose] as const;
}

/** Closes a popover on a click outside it and on Escape. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

const POPOVER =
  'absolute z-40 mt-2 rounded-[24px] bg-white p-4 ring-1 ring-[#E6E9EF] ring-inset shadow-[0_24px_48px_-24px_rgb(30_42_94/0.35)]';

function CoverPicker({ cover, onPick }: { cover: Cover; onPick: (c: Cover) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  return (
    <div ref={ref} className="absolute top-3 right-3 md:top-4 md:right-4">
      <button
        type="button"
        aria-label={t('studentHome.glass2.cover')}
        title={t('studentHome.glass2.cover')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex size-9 items-center justify-center rounded-full bg-white/80 text-ink backdrop-blur-md transition-colors hover:bg-white"
      >
        <PencilSimpleIcon size={16} weight="fill" aria-hidden />
      </button>
      {open && (
        <div className={`${POPOVER} right-0 w-[296px]`}>
          <p className="mb-3 text-[14px] font-semibold text-ink">
            {t('studentHome.glass2.coverTitle')}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {COVER_NAMES.map((c) => (
              <button
                key={c}
                type="button"
                aria-pressed={c === cover}
                onClick={() => {
                  onPick(c);
                  setOpen(false);
                }}
                className="flex flex-col items-center gap-1"
              >
                <span
                  className={`block h-12 w-full rounded-[14px] ${c === cover ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                  style={{ background: COVERS[c] }}
                />
                <span className="text-[12px] font-medium text-ink-soft">
                  {t(`studentHome.glass2.covers.${c}`)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Where the stickers sit around the face, like die-cut stickers on a photo (ref. «Kaia Bloom»). */
const STICKER_SPOTS = [
  'top-[-12px] left-[-20px] w-[46px] rotate-[-14deg]',
  'top-[-4px] right-[-22px] w-[42px] rotate-[12deg]',
  'bottom-[2px] left-[-16px] w-[36px] rotate-[-8deg]',
] as const;

/** The student's stickers: the buddy worn, then the others bought; three to start with. */
function useStickers(memberId: string): { stickers: CutSticker[]; frame: string | null } {
  const wallet = useRewardWallet(memberId);
  const worn = wallet.data?.worn.buddy;
  const owned = (wallet.data?.owned ?? []).filter((i) => i in REWARD_BUDDIES);
  const ids = [...new Set([...(worn ? [worn] : []), ...owned])];
  const mine = ids.map((i) => REWARD_BUDDIES[i]).filter((s): s is CutSticker => Boolean(s));
  const stickers = [...mine, 'rocket', 'books', 'glowing-star']
    .filter((s, i, all) => all.indexOf(s) === i)
    .slice(0, 3) as CutSticker[];
  return { stickers, frame: wallet.data?.worn.frame ?? null };
}

/** The face cut into the cover: the initials (no photos of children), the frame, the stickers, «+». */
function Face({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const { stickers, frame } = useStickers(ctx.child.memberId);
  const ring = frame ? REWARD_FRAMES[frame] : undefined;
  const initials = `${ctx.child.firstName[0] ?? ''}${ctx.child.lastName[0] ?? ''}`.toUpperCase();
  return (
    <div className="relative z-10 -mt-[52px] size-[104px] shrink-0 md:-mt-[64px] md:size-[128px]">
      <span className="absolute inset-0 rounded-full bg-white p-[5px]">
        <span
          className="block size-full rounded-full"
          style={ring ? { background: ring, padding: 3 } : undefined}
        >
          <span
            className="flex size-full items-center justify-center rounded-full font-display text-[36px] font-semibold tracking-[-0.02em] ring-[3px] ring-white md:text-[42px]"
            style={{
              backgroundImage: `linear-gradient(135deg, ${tones.blue.from} 0%, ${tones.violet.from} 100%)`,
              color: tones.violet.vivid,
            }}
          >
            {initials}
          </span>
        </span>
      </span>
      {stickers.map((s, i) => (
        <span
          key={s}
          className={`motion-pop pointer-events-none absolute ${STICKER_SPOTS[i]}`}
          style={{ '--delay': `${0.15 + i * 0.08}s` } as CSSProperties}
        >
          <Sticker name={s} size={46} className="h-auto w-full" />
        </span>
      ))}
      <Link
        to="/rewards"
        aria-label={t('studentHome.glass2.decorate')}
        title={t('studentHome.glass2.decorate')}
        className="absolute -bottom-1 left-1/2 flex size-8 -translate-x-1/2 items-center justify-center rounded-full bg-primary text-white ring-4 ring-white transition-transform duration-300 ease-spring hover:scale-110"
      >
        <PlusIcon size={15} weight="bold" aria-hidden />
      </Link>
    </div>
  );
}

const TAG =
  'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold transition-colors';

/** «Як ти?» as a button: the faces float up to choose from, the week under them, the way to «Турбота». */
function MoodButton({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const history = useMoodHistory(ctx.child.memberId, 7);
  const setMood = useSetMood();
  const online = useIsOnline();
  const today = todayIso();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Mood>();
  const ref = useDismiss(open, () => setOpen(false));
  const saved = history.data?.find((d) => d.day === today)?.mood;
  const mood = picked ?? saved;
  const byDay = new Map((history.data ?? []).map((d) => [d.day, d.mood]));
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i - 6));
  const choose = (m: Mood) => {
    setPicked(m);
    setMood.mutate({ schoolId: ctx.schoolId, mood: m });
  };
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`${TAG} ${mood ? '' : 'bg-[#F3F5F9] text-ink-soft hover:bg-[#EAEDF3]'}`}
        style={
          mood
            ? {
                backgroundColor: tones[MOOD_TONES[mood]].soft,
                color: tones[MOOD_TONES[mood]].vivid,
              }
            : undefined
        }
      >
        {mood ? (
          <MoodFace mood={mood} size={20} />
        ) : (
          <SmileyIcon size={17} weight="fill" aria-hidden className="text-tone-yellow-vivid" />
        )}
        {mood ? t(`care.moods.${mood}`) : t('studentHome.glass2.mood.ask')}
      </button>
      {open && (
        <div className={`${POPOVER} left-0 w-[min(340px,calc(100vw-48px))]`}>
          <p className="text-[15px] font-semibold text-ink">{t('studentHome.glass2.mood.title')}</p>
          <div className="mt-3 grid grid-cols-6 gap-1">
            {MOODS.map((m, i) => (
              <button
                key={m}
                type="button"
                aria-pressed={mood === m}
                aria-label={t(`care.moods.${m}`)}
                title={t(`care.moods.${m}`)}
                onClick={() => choose(m)}
                className="motion-pop flex flex-col items-center gap-1 rounded-[16px] py-1.5 transition-[background-color,transform] duration-300 ease-spring hover:-translate-y-1"
                style={
                  {
                    '--delay': `${i * 0.045}s`,
                    backgroundColor: mood === m ? tones[MOOD_TONES[m]].soft : undefined,
                  } as CSSProperties
                }
              >
                <MoodFace mood={m} size={38} />
                <span className="text-[10.5px] leading-3 font-semibold text-ink-soft">
                  {t(`care.moods.${m}`)}
                </span>
              </button>
            ))}
          </div>
          {mood && (
            <div
              className="mt-3 rounded-[16px] px-3 py-2.5 text-[13px] leading-[18px] text-ink-soft"
              style={{ backgroundColor: tones[MOOD_TONES[mood]].soft }}
            >
              {!online
                ? t('care.mood.queued')
                : HARD_MOODS.has(mood)
                  ? t('care.mood.hard')
                  : t('care.mood.good')}
              {HARD_MOODS.has(mood) && (
                <span className="mt-2 flex flex-wrap gap-2">
                  <Link
                    to="/care"
                    search={{ open: 'calm' }}
                    className={buttonClass('outline', 'sm')}
                  >
                    {t('care.actions.calm')}
                  </Link>
                  <Link to="/care" className={buttonClass('primary', 'sm')}>
                    {t('care.actions.write')}
                  </Link>
                </span>
              )}
            </div>
          )}
          <p className="mt-4 mb-2 text-[12px] font-semibold text-faint">
            {t('studentHome.glass2.mood.week')}
          </p>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const m = byDay.get(d) ?? (d === today ? mood : undefined);
              return (
                <span key={d} className="flex flex-col items-center gap-1">
                  {m ? (
                    <MoodFace mood={m} size={24} />
                  ) : (
                    <span className="size-6 rounded-full bg-[#F0F1F5]" />
                  )}
                  <span
                    className={`text-[11px] ${d === today ? 'font-semibold text-ink' : 'text-faint'}`}
                  >
                    {weekdayShort(d)}
                  </span>
                </span>
              );
            })}
          </div>
          <Link
            to="/care"
            className="mt-4 flex items-center justify-between rounded-[14px] bg-[#F5F6F9] px-3 py-2.5 text-[13px] font-semibold text-ink transition-colors hover:bg-[#EEF0F4]"
          >
            {t('studentHome.glass2.mood.more')}
            <CaretRightIcon size={12} weight="bold" aria-hidden />
          </Link>
        </div>
      )}
    </div>
  );
}

/** The tags under the name: the clubs, a birthday near (only then), the mood. */
function Tags({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const clubs = useClubs(ctx.schoolId);
  const memberships = useClubMemberships([ctx.child.memberId]);
  const birthdays = useBirthdays(ctx.schoolId, 14);
  const today = todayIso();
  const mine = new Set(
    (memberships.data ?? [])
      .filter((m) => m.studentId === ctx.child.memberId && m.status === 'active')
      .map((m) => m.clubId),
  );
  const myClubs = (clubs.data ?? []).filter((c) => mine.has(c.id));
  const birthday = (birthdays.data ?? []).find((b) => b.memberId === ctx.child.memberId);
  const tags: ReactNode[] = myClubs.slice(0, 3).map((c) => (
    <Link
      key={c.id}
      to="/clubs"
      className={TAG}
      style={{ backgroundColor: tones.teal.soft, color: tones.teal.vivid }}
    >
      <PuzzlePieceIcon size={15} weight="fill" aria-hidden />
      {c.title}
    </Link>
  ));
  if (birthday)
    tags.push(
      <span
        key="birthday"
        className={TAG}
        style={{ backgroundColor: tones.pink.soft, color: tones.pink.vivid }}
      >
        <CakeIcon size={15} weight="fill" aria-hidden />
        {birthday.onDate === today
          ? t('studentHome.glass2.birthdayToday')
          : t('studentHome.glass2.birthday', { date: dayMonthLong(birthday.onDate) })}
      </span>,
    );
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tags}
      <MoodButton ctx={ctx} />
    </div>
  );
}

export function ProfileHeader({
  ctx,
  className = '',
}: {
  ctx: StudentContext;
  className?: string;
}) {
  const { t } = useTranslation();
  const age = useAge();
  const [cover, setCover] = useCover(ctx.child.memberId);
  return (
    <section className={`relative min-w-0 rounded-[32px] bg-white ${className}`}>
      <div
        className="relative h-[112px] rounded-t-[32px] md:h-[150px]"
        style={{ background: COVERS[cover] }}
      >
        <span className="absolute top-3 left-3 inline-flex h-9 items-center gap-2 rounded-full bg-white/80 px-3.5 text-[13.5px] font-semibold text-ink backdrop-blur-md md:top-4 md:left-4">
          <CalendarDotsIcon size={16} weight="fill" aria-hidden className="text-tone-blue-vivid" />
          {dayLong(todayIso())}
        </span>
        <CoverPicker cover={cover} onPick={setCover} />
      </div>
      <div className="grid grid-cols-1 gap-5 px-4 pb-5 md:px-6 md:pb-6 lg:grid-cols-[minmax(0,1fr)_1px_minmax(300px,380px)] lg:gap-6">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:gap-5">
          <Face ctx={ctx} />
          <div className="flex min-w-0 flex-col gap-3 sm:pt-4">
            <div className="min-w-0">
              <p className="-ml-0.5 inline-block -rotate-2 font-hand text-[28px] leading-8 font-bold text-[#5B4F9A]">
                {t('studentHome.hi')}
              </p>
              <h1
                className={`font-display font-semibold tracking-[-0.03em] text-ink ${age === 'junior' ? 'text-[40px] leading-[46px]' : 'text-[34px] leading-[40px] md:text-[38px] md:leading-[44px]'}`}
              >
                {ctx.child.firstName} {ctx.child.lastName}
              </h1>
              <p className="mt-1 text-[14px] leading-5 text-muted">
                {[
                  ctx.child.class &&
                    t('studentHome.glass2.classOf', { label: ctx.child.class.label }),
                  ctx.schoolName,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <Tags ctx={ctx} />
          </div>
        </div>
        <span aria-hidden className="hidden bg-[#E9ECF1] lg:block" />
        <div className="min-w-0 border-t border-[#E9ECF1] pt-5 lg:border-0">
          <NowCard ctx={ctx} flat />
        </div>
      </div>
    </section>
  );
}
